import React, { useState } from 'react';
import { useQueryClient } from 'react-query';

import { CheckOutlined, SearchOutlined } from '@ant-design/icons';
import classNames from 'classnames';
import styled from 'styled-components';

import { COMING_SOON_INTERESTS_QUERY_KEY, createComingSoonFeatureInterest } from 'apis/ComingSoonFeaturesAPI';
import useRecommendationsContext from 'components/AiCompetitiveIntelligence/RecommendationsContext';
import { useRootContext } from 'components/layout/RootContext';
import Button from 'components/lib/Button';
import Input from 'components/lib/Input';
import Modal from 'components/lib/Modal';
import TextArea from 'components/lib/TextArea';
import { useAppSelector } from 'store/hook';
import { ComingSoonFeatureConfig } from 'types/comingSoonFeatures';
import { SelectAdvancedFilterOptionType } from 'types/common';

import { MAX_PILOT_COMMUNITIES } from './constants';

const Subtitle = styled.div`
  font-family: var(--font-regular);
  font-weight: 500;
  font-size: 14px;
  color: var(--text-secondary);
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 20px 0 25px;
`;

const UserCard = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 15px 20px;
  border: 1px solid var(--line-gray);
  border-radius: 8px;
`;

const Avatar = styled.div`
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--button-blue);
  border-radius: 50%;
  color: var(--button-blue);
  font-size: 12px;
`;

const Email = styled.div`
  font-size: 12px;
  color: var(--text-secondary);
`;

const ListTitle = styled.div`
  padding-top: 20px;
  margin-bottom: 12px;
  border-top: 1px solid var(--line-gray);
  font-family: var(--font-bold);
  font-size: 12px;
  text-transform: uppercase;

  span {
    font-family: var(--font-regular);
    color: var(--text-secondary);
    text-transform: none;
  }
`;

const COMMUNITY_LIST_MAX_HEIGHT = '280px';

const CommunityList = styled.div`
  max-height: ${COMMUNITY_LIST_MAX_HEIGHT};
  overflow-y: auto;
`;

const CommunityRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 0 6px 25px;
  cursor: pointer;

  &.selected {
    padding-left: 0;
  }

  &.disabled {
    cursor: not-allowed;
    color: var(--text-secondary);
  }

  .anticon {
    color: var(--positive-green);
  }
`;

const Label = styled.div`
  font-size: 14px;
  margin-bottom: 10px;

  span {
    color: var(--text-secondary);
  }
`;

const StyledButton = styled(Button)`
  padding-left: 30px;
  padding-right: 30px;
`;

type Props = {
  feature: ComingSoonFeatureConfig;
  onClose: () => void;
};

const ComingSoonInterestModal = ({ feature, onClose }: Props) => {
  const { showMessage } = useRootContext();
  const queryClient = useQueryClient();
  const { currentUser } = useAppSelector((state) => state.auth);
  const { communityOptions } = useRecommendationsContext();
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);

  const filteredOptions = communityOptions.filter((option) =>
    String(option.label).toLowerCase().includes(search.trim().toLowerCase())
  );
  const selectedOptions = filteredOptions.filter((option) => selectedIds.includes(Number(option.value)));
  const unselectedOptions = filteredOptions.filter((option) => !selectedIds.includes(Number(option.value)));
  const isLimitReached = selectedIds.length >= MAX_PILOT_COMMUNITIES;
  const userName = currentUser?.fname || currentUser?.FullName || currentUser?.email;

  const toggleCommunity = (id: number) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter((selectedId) => selectedId !== id));
    } else if (!isLimitReached) {
      setSelectedIds([...selectedIds, id]);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await createComingSoonFeatureInterest({ feature: feature.key, communities: selectedIds, note: note.trim() });
      await queryClient.invalidateQueries({ queryKey: [COMING_SOON_INTERESTS_QUERY_KEY] });
      onClose();
    } catch (err) {
      showMessage('error', 'Failed to submit your interest.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const renderCommunityRow = (option: SelectAdvancedFilterOptionType, selected: boolean) => (
    <CommunityRow
      key={option.value}
      className={classNames({ selected, disabled: !selected && isLimitReached })}
      data-test-id={`coming-soon-modal-community-${option.value}`}
      onClick={() => toggleCommunity(Number(option.value))}
    >
      {selected && <CheckOutlined />}
      {option.label}
    </CommunityRow>
  );

  return (
    <Modal
      open
      centered
      destroyOnClose
      className="compact bare-header"
      data-test-id={`coming-soon-modal-${feature.key}`}
      title={
        <div>
          <div>{feature.isPilot ? `Apply to the ${feature.label} pilot` : `Get notified about ${feature.label}`}</div>
          <Subtitle>
            {feature.isPilot
              ? 'Select up to two communities to pilot with. We’ll begin submitting inquiries across their markets this month.'
              : `We’ll let you know the moment ${feature.label} opens for pilots - you’ll be first in line, before general availability.`}
          </Subtitle>
        </div>
      }
      onCancel={onClose}
      footer={[
        <StyledButton
          key="submit"
          data-test-id="coming-soon-modal-submit"
          type="primary"
          loading={loading}
          disabled={loading || (feature.isPilot && selectedIds.length === 0)}
          onClick={handleSubmit}
        >
          {feature.isPilot ? 'Submit Application' : 'Notify Me'}
        </StyledButton>,
        <StyledButton key="cancel" data-test-id="coming-soon-modal-cancel" disabled={loading} onClick={onClose}>
          Cancel
        </StyledButton>,
      ]}
    >
      <Body>
        {feature.isPilot ? (
          <>
            <Input
              prefix={<SearchOutlined />}
              placeholder="Search communities"
              data-test-id="coming-soon-modal-search"
              value={search}
              onChange={(e: any) => setSearch(e.target.value)}
            />
            <CommunityList>
              <ListTitle>
                Selected <span>(max {MAX_PILOT_COMMUNITIES} for pilot)</span>
              </ListTitle>
              {selectedOptions.map((option) => renderCommunityRow(option, true))}
              <ListTitle>Unselected</ListTitle>
              {unselectedOptions.map((option) => renderCommunityRow(option, false))}
            </CommunityList>
          </>
        ) : (
          <UserCard>
            <Avatar>{userName?.charAt(0).toUpperCase()}</Avatar>
            <div>
              <div>{userName}</div>
              <Email>{currentUser?.email}</Email>
            </div>
          </UserCard>
        )}
        <div>
          <Label>
            {feature.isPilot
              ? 'What would you like to learn about your market?'
              : 'What would you like this feature to answer for you?'}{' '}
            <span>(optional)</span>
          </Label>
          <TextArea
            rows={3}
            data-test-id="coming-soon-modal-note"
            placeholder={feature.notePlaceholder}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </div>
      </Body>
    </Modal>
  );
};

export default ComingSoonInterestModal;
